import { EventPoster } from '@/features/events/shared/EventPoster';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/async-state';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Icon } from '@/components/ui/icon';
import { AppScreen } from '@/components/ui/scene';
import { Text } from '@/components/ui/text';
import { Textarea } from '@/components/ui/textarea';
import { BusinessShellNav } from './BusinessShellNav';
import { WorkspaceAccessState } from './WorkspaceAccessState';
import { useAuth } from '@/lib/auth/auth';
import { getAPIErrorRecoveryCopy } from '@/lib/api/apiErrorCopy';
import { cn } from '@/lib/utils';
import { useBusinessWorkspace } from './useBusinessWorkspace';
import { useCancelEvent } from '@/features/events/useCancelEvent';
import { useDeleteEvent } from '@/features/events/useDeleteEvent';
import { useVenueEvents } from '@/features/venues/useVenueEvents';
import { trackBusinessActionTapped } from '@/lib/analytics/posthog';
import { formatWorkspaceEventTime, formatWorkspaceMetric } from '@/features/workspaces/workspace';
import { Redirect, router } from 'expo-router';
import {
  ArrowUpRightIcon,
  CalendarPlusIcon,
  LockKeyholeIcon,
  PencilIcon,
  PlusIcon,
  Trash2Icon,
  UsersIcon,
  XCircleIcon,
} from 'lucide-react-native';
import * as React from 'react';
import { Pressable, ScrollView, View } from 'react-native';

export function WorkspaceEventsScene({
  organizationID,
  showSectionNav = true,
}: {
  organizationID: string;
  showSectionNav?: boolean;
}) {
  const session = useAuth((state) => state.session);
  const { organizationQuery, activeVenue, canManageEvents, orgAccessEnabled } =
    useBusinessWorkspace(organizationID);
  const cancelEvent = useCancelEvent();
  const deleteEvent = useDeleteEvent();
  const venueEventsQuery = useVenueEvents(activeVenue?.venue_id ?? null);
  const [cancelTarget, setCancelTarget] = React.useState<{ id: string; title: string } | null>(null);
  const [cancelMessage, setCancelMessage] = React.useState('');
  const [deleteTarget, setDeleteTarget] = React.useState<{ id: string; title: string } | null>(null);

  const handleCancelEvent = React.useCallback(
    (eventID: string, title: string) => {
      if (!activeVenue?.venue_id) return;
      setCancelTarget({ id: eventID, title });
      setCancelMessage('');
    },
    [activeVenue?.venue_id]
  );

  const handleDeleteEvent = React.useCallback(
    (eventID: string, title: string) => {
      if (!activeVenue?.venue_id) return;
      setDeleteTarget({ id: eventID, title });
    },
    [activeVenue?.venue_id]
  );

  const submitCancelEvent = React.useCallback(() => {
    if (!activeVenue?.venue_id || !cancelTarget) return;
    const message = cancelMessage.trim();
    cancelEvent.mutate({
      eventID: cancelTarget.id,
      message: message.length > 0 ? message : null,
      venueID: activeVenue.venue_id,
    });
    setCancelTarget(null);
    setCancelMessage('');
  }, [activeVenue?.venue_id, cancelEvent, cancelMessage, cancelTarget]);

  const submitDeleteEvent = React.useCallback(() => {
    if (!activeVenue?.venue_id || !deleteTarget) return;
    deleteEvent.mutate({ eventID: deleteTarget.id, venueID: activeVenue.venue_id });
    setDeleteTarget(null);
  }, [activeVenue?.venue_id, deleteEvent, deleteTarget]);

  const closeCancelDialog = React.useCallback(() => {
    if (cancelEvent.isPending) return;
    setCancelTarget(null);
    setCancelMessage('');
  }, [cancelEvent.isPending]);

  const closeDeleteDialog = React.useCallback(() => {
    if (deleteEvent.isPending) return;
    setDeleteTarget(null);
  }, [deleteEvent.isPending]);

  const isCancelDialogOpen = Boolean(cancelTarget);
  const isDeleteDialogOpen = Boolean(deleteTarget);
  const trackEventsAction = React.useCallback(
    (action: Parameters<typeof trackBusinessActionTapped>[0]['action'], eventID?: string | null) => {
      trackBusinessActionTapped({
        action,
        eventID,
        organizationID,
        source: 'business_events',
        venueID: activeVenue?.venue_id ?? null,
      });
    },
    [activeVenue?.venue_id, organizationID]
  );

  const handleCancelDialogOpenChange = React.useCallback(
    (open: boolean) => {
      if (!open) closeCancelDialog();
    },
    [closeCancelDialog]
  );

  const handleDeleteDialogOpenChange = React.useCallback(
    (open: boolean) => {
      if (!open) closeDeleteDialog();
    },
    [closeDeleteDialog]
  );

  if (!session) {
    return (
      <Redirect
        href={{ pathname: '/sign-in', params: { next: `/workspaces/${organizationID}/events` } }}
      />
    );
  }

  if (!orgAccessEnabled) {
    return <Redirect href={'/workspaces' as never} />;
  }

  const events = venueEventsQuery.data?.events ?? [];
  const cancelledCount = events.filter((event) => event.is_cancelled).length;
  const liveCount = events.length - cancelledCount;
  const rsvpCount = events.reduce((sum, event) => sum + event.rsvp_count, 0);

  const statsMetrics = [
    { label: 'Scheduled', value: formatWorkspaceMetric(events.length) },
    { label: 'Live', value: formatWorkspaceMetric(liveCount) },
    { label: 'Cancelled', value: formatWorkspaceMetric(cancelledCount) },
    { label: 'RSVPs', value: formatWorkspaceMetric(rsvpCount) },
  ];

  return (
    <AppScreen>
      <ScrollView
        className="flex-1"
        contentContainerStyle={{ paddingBottom: 40 }}
        showsVerticalScrollIndicator={false}>
        <View style={{ paddingHorizontal: 16, paddingTop: 16 }}>
          {organizationQuery.isPending ? <LoadingState label="Loading events..." /> : null}
          {organizationQuery.isError ? (
            <ErrorState
              body={getAPIErrorRecoveryCopy(
                organizationQuery.error,
                'workspace',
                'Unable to load events.'
              )}
              onAction={() => organizationQuery.refetch()}
            />
          ) : null}
        </View>

        {organizationQuery.data ? (
          <View>
            <View style={{ paddingHorizontal: 16, paddingTop: 16 }}>
              <BusinessShellNav
                organizationID={organizationID}
                organizationName={organizationQuery.data.name}
                viewerRole={organizationQuery.data.viewer_role}
                activeTab="events"
                activeVenue={activeVenue}
                showSectionNav={showSectionNav}
              />
            </View>

            {cancelEvent.error ? (
              <View style={{ paddingHorizontal: 16, paddingTop: 12 }}>
                <ErrorState
                  title="Could not cancel event"
                  body={getAPIErrorRecoveryCopy(
                    cancelEvent.error,
                    'workspace',
                    'Unable to cancel.'
                  )}
                />
              </View>
            ) : null}
            {deleteEvent.error ? (
              <View style={{ paddingHorizontal: 16, paddingTop: 12 }}>
                <ErrorState
                  title="Could not delete event"
                  body={getAPIErrorRecoveryCopy(
                    deleteEvent.error,
                    'workspace',
                    'Unable to delete.'
                  )}
                />
              </View>
            ) : null}

            {!activeVenue ? (
              <View style={{ paddingHorizontal: 16, paddingTop: 24 }}>
                <WorkspaceAccessState
                  icon={
                    organizationQuery.data.viewer_role === 'owner'
                      ? CalendarPlusIcon
                      : LockKeyholeIcon
                  }
                  title={
                    organizationQuery.data.viewer_role === 'owner'
                      ? 'No venue selected'
                      : 'No event access yet'
                  }
                  body={
                    organizationQuery.data.viewer_role === 'owner'
                      ? 'Pick a venue for this organization to see its calendar and start publishing events.'
                      : 'You need venue access before this organization can show you event operations. Ask an owner or manager to assign you a venue.'
                  }
                />
              </View>
            ) : (
              <>
                {/* ── Stats band — edge to edge ─────────────────────── */}
                <View className="mt-6 border-y border-border/60">
                  <View className="flex-row">
                    {statsMetrics.map((metric, index) => (
                      <View
                        key={metric.label}
                        className={cn(
                          'flex-1 items-center py-4',
                          index > 0 && 'border-l border-border/60'
                        )}>
                        <Text className="text-[22px] font-bold leading-none text-foreground">
                          {metric.value}
                        </Text>
                        <Text variant="small" className="mt-1.5 text-muted-foreground">
                          {metric.label}
                        </Text>
                      </View>
                    ))}
                  </View>
                </View>

                {/* ── Schedule header ───────────────────────────────── */}
                <View
                  style={{ paddingHorizontal: 16, paddingTop: 28, paddingBottom: 12 }}
                  className="flex-row items-center justify-between">
                  <Eyebrow>Schedule</Eyebrow>
                  {canManageEvents ? (
                    <Pressable
                      className="flex-row items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-3 py-1.5 active:opacity-75"
                      disabled={cancelEvent.isPending || deleteEvent.isPending}
                      onPress={() => {
                        trackEventsAction('create_event');
                        router.push({
                          pathname: '/events/create',
                          params: {
                            venueId: activeVenue.venue_id,
                            venueName: activeVenue.venue_name,
                          },
                        });
                      }}>
                      <Icon as={PlusIcon} size={13} className="text-primary" />
                      <Text className="text-[13px] font-semibold text-primary">Create</Text>
                    </Pressable>
                  ) : null}
                </View>

                {/* ── Event list — flat, border-separated ──────────── */}
                <View className="border-t border-border/60">
                  {venueEventsQuery.isPending ? (
                    <View style={{ padding: 16 }}>
                      <LoadingState label="Loading events..." />
                    </View>
                  ) : venueEventsQuery.isError ? (
                    <View style={{ padding: 16 }}>
                      <ErrorState
                        body={getAPIErrorRecoveryCopy(
                          venueEventsQuery.error,
                          'workspace',
                          'Unable to load events.'
                        )}
                        onAction={() => venueEventsQuery.refetch()}
                      />
                    </View>
                  ) : events.length === 0 ? (
                    <View style={{ paddingVertical: 32, paddingHorizontal: 16 }}>
                      <EmptyState
                        title="No events yet"
                        body="Create the first event for this venue."
                      />
                    </View>
                  ) : (
                    events.map((event, index) => (
                      <EventRow
                        key={event.id}
                        event={event}
                        canManageEvents={canManageEvents}
                        isBusy={cancelEvent.isPending || deleteEvent.isPending}
                        isFirst={index === 0}
                        onCancel={() => handleCancelEvent(event.id, event.title)}
                        onDelete={() => handleDeleteEvent(event.id, event.title)}
                        onTrackAction={trackEventsAction}
                      />
                    ))
                  )}
                </View>
              </>
            )}
          </View>
        ) : null}
      </ScrollView>
      <CancelEventDialog
        eventTitle={cancelTarget?.title ?? ''}
        isBusy={cancelEvent.isPending}
        isOpen={isCancelDialogOpen}
        message={cancelMessage}
        onChangeMessage={setCancelMessage}
        onClose={closeCancelDialog}
        onConfirm={submitCancelEvent}
        onOpenChange={handleCancelDialogOpenChange}
      />
      <DeleteEventDialog
        eventTitle={deleteTarget?.title ?? ''}
        isBusy={deleteEvent.isPending}
        isOpen={isDeleteDialogOpen}
        onClose={closeDeleteDialog}
        onConfirm={submitDeleteEvent}
        onOpenChange={handleDeleteDialogOpenChange}
      />
    </AppScreen>
  );
}

// ── Private components ────────────────────────────────────────────────────────

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <Text className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
      {children}
    </Text>
  );
}

function EventRow({
  event,
  canManageEvents,
  isBusy,
  isFirst,
  onCancel,
  onDelete,
  onTrackAction,
}: {
  event: NonNullable<ReturnType<typeof useVenueEvents>['data']>['events'][number];
  canManageEvents: boolean;
  isBusy: boolean;
  isFirst: boolean;
  onCancel: () => void;
  onDelete: () => void;
  onTrackAction: (
    action: Parameters<typeof trackBusinessActionTapped>[0]['action'],
    eventID?: string | null
  ) => void;
}) {
  return (
    <View
      className={cn(
        'flex-row gap-3 px-4 py-3.5',
        !isFirst && 'border-t border-border/50'
      )}>
      <EventPoster event={event} className="h-[110px] w-[82px] rounded-lg" showMeta={false} />

      <View className="flex-1 justify-between py-0.5">
        <View className="gap-1.5">
          <View className="flex-row items-start justify-between gap-2">
            <Text
              className="flex-1 text-[15px] font-semibold leading-[21px] text-foreground"
              numberOfLines={2}>
              {event.title}
            </Text>
            <EventStatusPill cancelled={event.is_cancelled} />
          </View>

          <Text variant="small" className="text-muted-foreground">
            {formatWorkspaceEventTime(event.starts_at)}
          </Text>

          <View className="flex-row items-center gap-1.5">
            <Icon as={UsersIcon} size={11} className="text-muted-foreground/60" />
            <Text variant="small" className="text-muted-foreground">
              {formatWorkspaceMetric(event.rsvp_count)} going
            </Text>
            {event.source_fixture_id ? (
              <View className="ml-0.5 rounded border border-primary/20 bg-primary/10 px-1.5 py-px">
                <Text className="text-[10px] font-semibold text-primary">
                  {event.is_fixture_managed ? 'Fixture' : 'Fixture edit'}
                </Text>
              </View>
            ) : null}
          </View>
        </View>

        <View className="flex-row flex-wrap gap-1.5 pt-1">
          <ActionChip
            icon={ArrowUpRightIcon}
            label="View"
            onPress={() => {
              onTrackAction('view_event', event.id);
              router.push(`/event/${event.id}` as never);
            }}
          />
          {canManageEvents ? (
            <>
              <ActionChip
                icon={PencilIcon}
                label="Edit"
                disabled={isBusy}
                onPress={() => {
                  onTrackAction('edit_event', event.id);
                  router.push(`/events/${event.id}/edit` as never);
                }}
              />
              <ActionChip
                icon={event.is_cancelled ? Trash2Icon : XCircleIcon}
                label={event.is_cancelled ? 'Delete' : 'Cancel'}
                disabled={isBusy}
                destructive
                onPress={() => {
                  onTrackAction(event.is_cancelled ? 'delete_event' : 'cancel_event', event.id);
                  if (event.is_cancelled) {
                    onDelete();
                    return;
                  }
                  onCancel();
                }}
              />
            </>
          ) : null}
        </View>
      </View>
    </View>
  );
}

function EventStatusPill({ cancelled }: { cancelled: boolean }) {
  return (
    <View
      className={cn(
        'mt-0.5 rounded-full px-2 py-0.5',
        cancelled ? 'bg-destructive/15' : 'bg-emerald-500/15'
      )}>
      <Text
        className={cn(
          'text-[10px] font-semibold',
          cancelled ? 'text-destructive' : 'text-emerald-400'
        )}>
        {cancelled ? 'Cancelled' : 'Live'}
      </Text>
    </View>
  );
}

function CancelEventDialog({
  eventTitle,
  isBusy,
  isOpen,
  message,
  onChangeMessage,
  onClose,
  onConfirm,
  onOpenChange,
}: {
  eventTitle: string;
  isBusy: boolean;
  isOpen: boolean;
  message: string;
  onChangeMessage: (value: string) => void;
  onClose: () => void;
  onConfirm: () => void;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent avoidKeyboard className="gap-0 overflow-hidden p-0">
        <DialogHeader className="border-b border-border px-5 pb-4 pr-12 pt-5">
          <DialogTitle>Cancel event</DialogTitle>
          <DialogDescription className="leading-5">
            Guests will see this event as cancelled. Add a short note if there is something they
            should know.
          </DialogDescription>
        </DialogHeader>
        <View className="gap-3 px-5 py-4">
          <View className="rounded-md border border-border bg-surface-alt px-3 py-2">
            <Text variant="small" className="font-semibold text-foreground" numberOfLines={2}>
              {eventTitle}
            </Text>
          </View>
          <View className="gap-1.5">
            <Text variant="small" className="font-semibold text-foreground">
              Optional note
            </Text>
            <Textarea
              value={message}
              onChangeText={onChangeMessage}
              editable={!isBusy}
              placeholder="Door time changed, weather update, or refund note"
              className="min-h-28"
            />
          </View>
        </View>
        <DialogFooter className="border-t border-border px-5 py-4">
          <Button variant="outline" disabled={isBusy} onPress={onClose}>
            <Text>Keep event</Text>
          </Button>
          <Button variant="destructive" disabled={isBusy} onPress={onConfirm}>
            <Text>{isBusy ? 'Cancelling...' : 'Cancel event'}</Text>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteEventDialog({
  eventTitle,
  isBusy,
  isOpen,
  onClose,
  onConfirm,
  onOpenChange,
}: {
  eventTitle: string;
  isBusy: boolean;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="gap-0 overflow-hidden p-0">
        <DialogHeader className="border-b border-border px-5 pb-4 pr-12 pt-5">
          <DialogTitle>Delete event</DialogTitle>
          <DialogDescription className="leading-5">
            This removes the cancelled event from the venue workspace. This cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <View className="px-5 py-4">
          <View className="rounded-md border border-border bg-surface-alt px-3 py-2">
            <Text variant="small" className="font-semibold text-foreground" numberOfLines={2}>
              {eventTitle}
            </Text>
          </View>
        </View>
        <DialogFooter className="border-t border-border px-5 py-4">
          <Button variant="outline" disabled={isBusy} onPress={onClose}>
            <Text>Keep event</Text>
          </Button>
          <Button variant="destructive" disabled={isBusy} onPress={onConfirm}>
            <Text>{isBusy ? 'Deleting...' : 'Delete event'}</Text>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ActionChip({
  icon,
  label,
  onPress,
  disabled = false,
  destructive = false,
}: {
  icon: React.ComponentProps<typeof Icon>['as'];
  label: string;
  onPress: () => void;
  disabled?: boolean;
  destructive?: boolean;
}) {
  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      className={cn(
        'flex-row items-center gap-1.5 rounded-md border px-2.5 py-1.5 active:opacity-70',
        destructive
          ? 'border-destructive/25 bg-destructive/8'
          : 'border-border/80 bg-background',
        disabled && 'opacity-40'
      )}>
      <Icon
        as={icon}
        size={12}
        className={destructive ? 'text-destructive' : 'text-muted-foreground'}
      />
      <Text
        className={cn(
          'text-[12px] font-medium',
          destructive ? 'text-destructive' : 'text-foreground'
        )}>
        {label}
      </Text>
    </Pressable>
  );
}
